import {
  buildDatasourceExecutionNodeIdentity,
  normalizeDatasourceExecutionPlanTempTableFamily,
  normalizeDatasourceExecutionNodeSqlIdentity,
} from './datasourceExecutionGraph'
import { buildTemplateParamSafeKey } from './fieldIdentity'
import { sqlHasUnboundPlanTempTableDependencies } from './planTempTableDetection'
import {
  normalizeWorksheetPlannerMetadata,
  type WorksheetPlannerMetadata,
} from './worksheetPlannerMetadata'

const hashStringToBase36 = (value: string): string => {
  let h1 = 2166136261
  let h2 = 2166136261 ^ 0x9e3779b9
  const s = String(value ?? '')
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    h1 ^= c
    h1 = Math.imul(h1, 16777619)
    h2 ^= c
    h2 = Math.imul(h2, 16777619)
  }
  const a = (h1 >>> 0).toString(36).padStart(8, '0')
  const b = (h2 >>> 0).toString(36).padStart(8, '0')
  return `${a}${b}`
}

const stableStringify = (value: any): string => {
  const seen = new WeakSet<object>()
  const normalize = (current: any): any => {
    if (current === null || typeof current === 'undefined') return current
    if (typeof current !== 'object') return current
    if (seen.has(current as object)) return '[Circular]'
    seen.add(current as object)
    if (Array.isArray(current)) return current.map(normalize)
    const out: Record<string, any> = {}
    for (const key of Object.keys(current).sort()) out[key] = normalize(current[key])
    return out
  }
  try {
    return JSON.stringify(normalize(value))
  } catch {
    return String(value ?? '')
  }
}

const normalizeAggBaseIdentitySql = (sql: string | null | undefined): string => {
  return normalizeDatasourceExecutionNodeSqlIdentity(sql)
}

const normalizeComparableBindingValue = (raw: any): any => {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number' || typeof raw === 'boolean') return raw
  if (Array.isArray(raw)) return raw.map((value) => normalizeComparableBindingValue(value))
  if (raw && typeof raw === 'object') {
    const entries = Object.entries(raw as Record<string, any>)
      .sort(([left], [right]) => String(left).localeCompare(String(right)))
    const out: Record<string, any> = {}
    for (const [key, value] of entries) out[String(key)] = normalizeComparableBindingValue(value)
    return out
  }
  const text = String(raw ?? '').trim()
  if (!text) return text
  const unquoted = text.replace(/^"(.*)"$/s, '$1').replace(/^'(.*)'$/s, '$1')
  if (/^(true|false)$/i.test(unquoted)) return unquoted.toLowerCase() === 'true'
  const num = Number(unquoted)
  if (Number.isFinite(num)) return num
  return unquoted
}

const splitComparableInFilterValues = (raw: any[]): { values: any[]; includeNull: boolean } => {
  const values: any[] = []
  let includeNull = false
  for (const value of raw) {
    if (value === null || value === undefined) {
      includeNull = true
      continue
    }
    values.push(normalizeComparableBindingValue(value))
  }
  return { values, includeNull }
}

export const buildSqlExecutionBindingSignature = (input: {
  sql?: string | null
  filters?: any[]
  parameters?: Record<string, any>
  normalizeValue?: ((value: any) => any) | null
  splitInFilterValues?: ((values: any[]) => { values: any[]; includeNull: boolean }) | null
  plannerMetadata?: WorksheetPlannerMetadata | null
}): string | null => {
  return buildExecutionBindingSignatureInternal(input, { includeParameters: true })
}

const buildExecutionBindingSignatureInternal = (input: {
  sql?: string | null
  filters?: any[]
  parameters?: Record<string, any>
  normalizeValue?: ((value: any) => any) | null
  splitInFilterValues?: ((values: any[]) => { values: any[]; includeNull: boolean }) | null
  plannerMetadata?: WorksheetPlannerMetadata | null
}, opts?: { includeParameters?: boolean }): string | null => {
  const sql = String(input.sql ?? '').trim()
  if (!sql) return null
  const normalizeValue = input.normalizeValue ?? normalizeComparableBindingValue
  const splitInFilterValues = input.splitInFilterValues ?? splitComparableInFilterValues
  const plannerMetadata = normalizeWorksheetPlannerMetadata(input.plannerMetadata)
  const normalizedFilters = (Array.isArray(input.filters) ? input.filters : [])
    .map((filter: any, index: number) => {
      const scalarToken = `/*__FG_FILTER:${index}__*/`
      const tableToken = `__FG_FILTER_TABLE__${index}__`
      const partToken = `/*__FG_FILTER:${index}:`
      const boundTablePattern = new RegExp(`\\b__fg_ft_[a-z0-9_]+_${index}\\b`, 'i')
      const operator = String((filter as any)?.operator ?? '').trim().toUpperCase()
      const isTupleOperator = operator === 'ANY_TUPLE' || operator === 'NOT ANY_TUPLE'
      const hasMatchingBinding =
        sql.includes(scalarToken) ||
        sql.includes(tableToken) ||
        sql.includes(partToken) ||
        boundTablePattern.test(sql)
      if (!isTupleOperator && !hasMatchingBinding) return null
      const rawValue = (filter as any)?.value
      const normalizedValue = (() => {
        if (operator === 'IN' || operator === 'NOT IN') {
          const arr =
            Array.isArray(rawValue)
              ? rawValue
              : (rawValue === null || typeof rawValue === 'undefined')
                ? []
                : [rawValue]
          const { values, includeNull } = splitInFilterValues(arr as any[])
          return { values, includeNull }
        }
        return normalizeValue(rawValue)
      })()
      return {
        field_name: String((filter as any)?.field_name ?? '').trim().toLowerCase(),
        operator,
        filter_type: String((filter as any)?.filter_type ?? '').trim().toLowerCase() || null,
        aggregation: String((filter as any)?.aggregation ?? '').trim().toUpperCase() || null,
        context: Boolean((filter as any)?.context),
        is_measure: Boolean((filter as any)?.is_measure),
        kind: String((filter as any)?.kind ?? '').trim().toLowerCase() || null,
        value: normalizedValue,
      }
    })
    .filter(Boolean)
  const normalizedParameters =
    opts?.includeParameters === false
      ? []
      : Object.entries(input.parameters || {})
        .map(([key, value]) => {
          const keyText = String(key ?? '').trim()
          const safeKey = buildTemplateParamSafeKey(keyText)
          if (!safeKey) return null
          const paramToken = `/*__FG_PARAM:${safeKey}:`
          if (!sql.includes(paramToken)) return null
          return {
            key: keyText.toLowerCase(),
            value: normalizeValue(value),
          }
        })
        .filter((entry): entry is { key: string; value: any } => Boolean(entry))
        .sort((left, right) => left.key.localeCompare(right.key))
  if (normalizedFilters.length <= 0 && normalizedParameters.length <= 0 && !plannerMetadata) return null
  return buildStableExecutionIdentity(stableStringify({
    filters: normalizedFilters,
    parameters: normalizedParameters,
    plannerMetadata,
  }))
}

export const buildAggBaseExecutionBindingSignature = (input: {
  sql?: string | null
  filters?: any[]
  parameters?: Record<string, any>
  normalizeValue?: ((value: any) => any) | null
  splitInFilterValues?: ((values: any[]) => { values: any[]; includeNull: boolean }) | null
  plannerMetadata?: WorksheetPlannerMetadata | null
}): string | null => buildExecutionBindingSignatureInternal({
  ...input,
  // Aggregate-base reuse should only vary with actual bound filter-table inputs.
  // Planner metadata is allowed to churn across restore/prewarm vs foreground paths
  // without invalidating the durable aggregate-base identity.
  plannerMetadata: null,
}, { includeParameters: false })

export const isPersistedAggBaseSqlEligible = (
  sql: string | null | undefined,
  statementsRaw?: any,
): boolean => {
  const text = String(sql ?? '').trim()
  if (!text) return false
  if (sqlHasUnboundPlanTempTableDependencies(text, statementsRaw)) return false
  // Persisted agg-base identities now include a normalized execution signature
  // for interaction-scoped filter temp tables and placeholders, so those
  // bindings are safe to persist and reuse across runs with equivalent filters.
  return true
}

export const buildAggBaseSqlSignature = (sql: string | null | undefined): string | null => {
  const text = normalizeAggBaseIdentitySql(sql)
  if (!text) return null
  return `AggBaseSql:${hashStringToBase36(text).slice(0, 24)}`
}

export const buildAggBasePlannerSignature = (
  sql: string | null | undefined,
  planHint?: {
    materializedSqlHash?: string | null
    materializedTable?: string | null
    aggBaseMaterializedTable?: string | null
  } | null,
): string | null => {
  const materializedSqlHash = String(planHint?.materializedSqlHash ?? '').trim() || null
  const materializedTableFamily = normalizeDatasourceExecutionPlanTempTableFamily(
    planHint?.materializedTable,
  )
  const aggBaseMaterializedTableFamily = normalizeDatasourceExecutionPlanTempTableFamily(
    planHint?.aggBaseMaterializedTable,
  )
  if (materializedSqlHash || materializedTableFamily || aggBaseMaterializedTableFamily) {
    return `AggBaseSql:${hashStringToBase36(stableStringify({
      mode: 'plan_materialization',
      nodeKind: 'aggregate_base',
      materializedSqlHash,
      materializedTableFamily,
      aggBaseMaterializedTableFamily,
    })).slice(0, 24)}`
  }
  return buildAggBaseSqlSignature(sql)
}

const buildSqlExecutionSignatureInternal = (input: {
  sql?: string | null
  dependencySignature?: string | null
  plannerMetadata?: WorksheetPlannerMetadata | null
}, prefix: string): string | null => {
  const sqlSignature = buildAggBaseSqlSignature(input.sql)
  if (!sqlSignature) return null
  const dependencySignature = String(input.dependencySignature ?? '').trim()
  const plannerMetadata = normalizeWorksheetPlannerMetadata(input.plannerMetadata)
  if (!dependencySignature && !plannerMetadata) return sqlSignature
  return `${prefix}:${hashStringToBase36(stableStringify({
    sqlSignature,
    dependencySignature: dependencySignature || null,
    plannerMetadata,
  })).slice(0, 24)}`
}

export const buildAggBaseExecutionSignature = (input: {
  sql?: string | null
  dependencySignature?: string | null
  plannerMetadata?: WorksheetPlannerMetadata | null
}): string | null => buildSqlExecutionSignatureInternal({
  ...input,
  plannerMetadata: null,
}, 'AggBaseExec')

export const buildSqlExecutionSignature = (input: {
  sql?: string | null
  dependencySignature?: string | null
  plannerMetadata?: WorksheetPlannerMetadata | null
}): string | null => buildSqlExecutionSignatureInternal(input, 'SqlExec')

export const buildStableExecutionIdentity = (value: string | null | undefined): string => {
  const text = String(value ?? '').trim()
  if (!text) return 'none'
  return hashStringToBase36(text).slice(0, 24)
}

export const buildPersistedAggBaseIdentity = (input: {
  datasetId?: string | null
  route?: string | null
  semanticSpecHash?: string | null
  workingSetSignature?: string | null
  plannerAggBaseSignature?: string | null
  aggBaseExecutionSignature?: string | null
}): string | null => {
  const plannerAggBaseSignature = String(input.plannerAggBaseSignature ?? '').trim()
  if (!plannerAggBaseSignature) return null
  return buildDatasourceExecutionNodeIdentity({
    nodeKind: 'aggregate_base',
    datasetId: String(input.datasetId ?? '').trim() || null,
    route: String(input.route ?? '').trim() || null,
    semanticSpecHash: String(input.semanticSpecHash ?? '').trim() || null,
    workingSetSignature: String(input.workingSetSignature ?? '').trim() || null,
    plannerNodeSignature: plannerAggBaseSignature,
    executionSignature: String(input.aggBaseExecutionSignature ?? '').trim() || null,
  })
}
